import React from 'react';
import { Box, BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import {
  Dashboard,
  Medication,
  AccountCircle,
  Inventory,
  People,
  Assessment,
  ViewKanban,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
interface BottomNavProps {
  role: 'admin' | 'doctor' | 'consultant';
}

interface NavTab {
  labelKey: string;
  icon: React.ReactNode;
  path: string;
}

const adminTabs: NavTab[] = [
  { labelKey: 'sidebar.dashboard', icon: <Dashboard />, path: '/admin/dashboard' },
  { labelKey: 'sidebar.medicationCatalogShort', icon: <Inventory />, path: '/admin/catalog' },
  { labelKey: 'sidebar.users', icon: <People />, path: '/admin/users' },
  { labelKey: 'sidebar.reports', icon: <Assessment />, path: '/admin/reports' },
];

export const BottomNav: React.FC<BottomNavProps> = ({ role }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();

  const getDoctorTabs = (): NavTab[] => [
    { labelKey: 'sidebar.dashboard', icon: <Dashboard />, path: '/doctor/dashboard' },
    ...(user?.canPrescribe
      ? [{ labelKey: 'sidebar.prescriptions', icon: <ViewKanban />, path: '/doctor/orders' }]
      : []),
    ...(user?.canViewPatients
      ? [{ labelKey: 'sidebar.patients', icon: <People />, path: '/doctor/patients' }]
      : []),
    { labelKey: 'sidebar.medicationsLookup', icon: <Medication />, path: '/doctor/medications' },
    { labelKey: 'sidebar.profile', icon: <AccountCircle />, path: '/doctor/profile' },
  ];

  const getConsultantTabs = (): NavTab[] => [
    { labelKey: 'sidebar.dashboard', icon: <Dashboard />, path: '/consultant/dashboard' },
    { labelKey: 'sidebar.medicationsLookup', icon: <Medication />, path: '/consultant/medications' },
  ];

  const getTabs = (): NavTab[] => {
    if (role === 'admin') return adminTabs;
    if (role === 'consultant') return getConsultantTabs();
    return getDoctorTabs();
  };

  const tabs = getTabs();

  const activeIndex = tabs.findIndex(
    (tab) => location.pathname === tab.path || location.pathname.startsWith(tab.path + '/')
  );
  const currentValue = activeIndex === -1 ? false : activeIndex;

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    const tab = tabs[newValue];
    if (tab) navigate(tab.path);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: { xs: 'block', md: 'none' },
        zIndex: (theme) => theme.zIndex.appBar,
        background: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(0,0,0,0.08)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <BottomNavigation
        value={currentValue}
        onChange={handleChange}
        sx={{
          height: 64,
          background: 'transparent',
          '& .MuiBottomNavigationAction-root': {
            color: 'text.secondary',
            minWidth: 0,
            padding: '6px 0',
            '&.Mui-selected': {
              color: 'primary.main',
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '10px',
            fontWeight: 500,
            '&.Mui-selected': {
              fontSize: '10px',
              fontWeight: 700,
            },
          },
        }}
      >
        {tabs.map((tab, index) => (
          <BottomNavigationAction
            key={tab.path}
            label={t(tab.labelKey)}
            icon={
              <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {currentValue === index && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -8,
                      width: 24,
                      height: 3,
                      borderRadius: '0 0 3px 3px',
                      bgcolor: 'primary.main',
                    }}
                  />
                )}
                {tab.icon}
              </Box>
            }
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};
